import React, { Suspense } from "react";
import { Navbar } from "./Navbar";

// Lazy load all sections
const HeroSection = React.lazy(() =>
  import("./HeroSection").then((module) => ({ default: module.HeroSection }))
);
const FeaturesSection = React.lazy(() =>
  import("./FeaturesSection").then((module) => ({
    default: module.FeaturesSection,
  }))
);
const PricingSection = React.lazy(() =>
  import("./PricingSection").then((module) => ({
    default: module.PricingSection,
  }))
);
const TestimonialSection = React.lazy(() =>
  import("./TestimonialSection").then((module) => ({
    default: module.TestimonialSection,
  }))
);
const CTASection = React.lazy(() =>
  import("./CTASection").then((module) => ({ default: module.CTASection }))
);

// Loading component with dark skeleton UI
const SectionSkeleton = () => (
  <div className="relative">
    {/* Shimmer overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-midnight-800/20 to-transparent animate-shimmer" />
    
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
      <div className="space-y-8">
        {/* Title skeleton */}
        <div className="h-10 bg-midnight-800/50 rounded-xl w-3/4 mx-auto animate-pulse" />
        
        {/* Subtitle skeleton */}
        <div className="h-5 bg-midnight-800/30 rounded-lg w-1/2 mx-auto animate-pulse" />
        
        {/* Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
          {[...Array(3)].map((_, i) => (
            <div 
              key={i} 
              className="h-64 bg-midnight-900/50 rounded-2xl border border-midnight-800/30 animate-pulse"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Footer component
const Footer = () => (
  <footer className="relative border-t border-midnight-800/50">
    {/* Background */}
    <div className="absolute inset-0 bg-midnight-950" />
    
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        {/* Brand */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-coral-500 to-coral-600 flex items-center justify-center shadow-glow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="text-xl font-bold text-white">FeedbackFlow</span>
          </div>
          <p className="text-midnight-400 max-w-sm leading-relaxed">
            Visual feedback and collaboration made simple. Leave comments directly on any website.
          </p>
          
          {/* Social links */}
          <div className="flex items-center gap-4 mt-6">
            <a href="#" className="w-10 h-10 rounded-lg bg-midnight-800/50 flex items-center justify-center text-midnight-400 hover:text-coral-400 hover:bg-midnight-800 transition-all">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
            </a>
            <a href="#" className="w-10 h-10 rounded-lg bg-midnight-800/50 flex items-center justify-center text-midnight-400 hover:text-coral-400 hover:bg-midnight-800 transition-all">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
            </a>
            <a href="#" className="w-10 h-10 rounded-lg bg-midnight-800/50 flex items-center justify-center text-midnight-400 hover:text-coral-400 hover:bg-midnight-800 transition-all">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
          </div>
        </div>
        
        {/* Links */}
        <div>
          <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Product</h4>
          <ul className="space-y-3">
            <li><a href="#features" className="text-midnight-400 hover:text-coral-400 transition-colors">Features</a></li>
            <li><a href="#pricing" className="text-midnight-400 hover:text-coral-400 transition-colors">Pricing</a></li>
            <li><a href="#" className="text-midnight-400 hover:text-coral-400 transition-colors">Changelog</a></li>
            <li><a href="#" className="text-midnight-400 hover:text-coral-400 transition-colors">Documentation</a></li>
          </ul>
        </div>
        
        <div>
          <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Company</h4>
          <ul className="space-y-3">
            <li><a href="#" className="text-midnight-400 hover:text-coral-400 transition-colors">About</a></li>
            <li><a href="#" className="text-midnight-400 hover:text-coral-400 transition-colors">Blog</a></li>
            <li><a href="#" className="text-midnight-400 hover:text-coral-400 transition-colors">Privacy</a></li>
            <li><a href="#" className="text-midnight-400 hover:text-coral-400 transition-colors">Terms</a></li>
          </ul>
        </div>
      </div>
      
      {/* Bottom bar */}
      <div className="mt-16 pt-8 border-t border-midnight-800/50 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-midnight-500 text-sm">
          © {new Date().getFullYear()} FeedbackFlow. All rights reserved.
        </p>
        <div className="flex items-center gap-2 text-sm text-midnight-500">
          <span className="w-2 h-2 rounded-full bg-mint-500 animate-pulse" />
          All systems operational
        </div>
      </div>
    </div>
  </footer>
);

export const LandingPage = () => {
  return (
    <div className="relative min-h-screen bg-midnight-950 overflow-x-hidden">
      {/* Global background effects */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid opacity-100" />
        
        {/* Radial gradients */}
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] radial-glow radial-glow-coral opacity-30" />
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] radial-glow radial-glow-electric opacity-20" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] radial-glow radial-glow-coral opacity-15" />
        
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }} />
      </div>
      
      {/* Content */}
      <div className="relative z-10">
        <Navbar />
        
        {/* Main content with padding for fixed navbar */}
        <main>
          <Suspense fallback={<SectionSkeleton />}>
            <HeroSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <FeaturesSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <PricingSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <TestimonialSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <CTASection />
          </Suspense>
        </main>
        
        <Footer />
      </div>
    </div>
  );
};
